/**
 * BrewingScreen - Minecraft-style brewing stand UI.
 *
 * Layout:
 *   [Ingredient]            <- top center
 *   [Bottle] --arrow--> [Output]
 *   [Fuel]                  <- bottom-left
 *
 * Recipes:
 *   Glass Bottle + Nether Wart           = Awkward Potion (base)
 *   Awkward Potion + Sugar (Apple)        = Speed Potion
 *   Awkward Potion + Blaze Powder         = Strength Potion
 *   Awkward Potion + Apple                = Healing Potion
 *   Awkward Potion + Blaze Powder(x2 use) = Fire Resistance
 *   Awkward Potion + Gold Ingot           = Night Vision
 *   Awkward Potion + Bone                 = Regeneration
 *
 * Brewing takes 20 seconds and consumes 1 Blaze Powder as fuel.
 */

import { renderBlockIcon, renderItemIcon } from './Hotbar.js';
import { ItemId } from '../items/ItemRegistry.js';
import { MAX_STACK_SIZE } from '../constants.js';

const SLOT_SIZE = 44;
const GAP = 2;
const COLS = 9;
const BREW_TIME = 20; // seconds

// ============================================================
// BREWING RECIPES
// ============================================================

const RECIPES = [
  // Base potion
  { bottle: ItemId.GLASS_BOTTLE,  ingredient: ItemId.NETHER_WART,  result: ItemId.AWKWARD_POTION },
  // Effect potions (from awkward base)
  { bottle: ItemId.AWKWARD_POTION, ingredient: ItemId.APPLE,         result: ItemId.POTION_SPEED },
  { bottle: ItemId.AWKWARD_POTION, ingredient: ItemId.BLAZE_POWDER,  result: ItemId.POTION_STRENGTH },
  { bottle: ItemId.AWKWARD_POTION, ingredient: ItemId.SPIDER_EYE,    result: ItemId.POTION_HEALING },
  { bottle: ItemId.AWKWARD_POTION, ingredient: ItemId.SLIME_BALL,    result: ItemId.POTION_FIRE_RESISTANCE },
  { bottle: ItemId.AWKWARD_POTION, ingredient: ItemId.GOLD_INGOT,    result: ItemId.POTION_NIGHT_VISION },
  { bottle: ItemId.AWKWARD_POTION, ingredient: ItemId.BONE,          result: ItemId.POTION_REGENERATION },
];

function findRecipe(bottleId, ingredientId) {
  return RECIPES.find(r => r.bottle === bottleId && r.ingredient === ingredientId) || null;
}

// ============================================================
// BrewingScreen CLASS
// ============================================================

export class BrewingScreen {
  constructor(inventory) {
    this.inventory = inventory;
    this.isOpen = false;
    this.heldItem = null;

    // Brewing slots
    this.ingredientSlot = null; // { itemId, count }
    this.bottleSlot = null;     // { itemId, count }
    this.fuelSlot = null;       // { itemId, count } (blaze powder)
    this.outputSlot = null;     // { itemId, count }

    // Brewing timer
    this._brewing = false;
    this._brewTimer = 0;
    this._brewProgress = 0;
    this._currentRecipe = null;

    this._build();
  }

  // ─── Build DOM ──────────────────────────────────────────────
  _build() {
    this.overlay = document.createElement('div');
    this.overlay.id = 'brewing-overlay';
    this.overlay.style.cssText = `
      position: fixed; top: 0; left: 0; right: 0; bottom: 0;
      background: rgba(0,0,0,0.55); z-index: 500;
      display: none; justify-content: center; align-items: center;
      cursor: default;
    `;

    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay) this.close();
    });

    this.panel = document.createElement('div');
    this.panel.style.cssText = `
      background: #7A7A7A;
      border: 4px solid #2A2A2A;
      border-top-color: #B0B0B0; border-left-color: #B0B0B0;
      padding: 12px 16px;
      font-family: 'Courier New', monospace; color: #E0E0E0;
      user-select: none; image-rendering: pixelated;
      box-shadow: 4px 4px 0 rgba(0,0,0,0.3), inset 0 0 0 2px #5A5A5A;
      min-width: 340px;
      position: relative;
    `;

    // Close button
    const closeBtn = document.createElement('div');
    closeBtn.textContent = 'X';
    closeBtn.style.cssText = `
      position: absolute; top: 6px; right: 10px;
      cursor: pointer; font-size: 16px; font-weight: bold;
      color: #CCCCCC; line-height: 1;
    `;
    closeBtn.addEventListener('click', () => this.close());
    closeBtn.addEventListener('mouseenter', () => { closeBtn.style.color = '#FFFFFF'; });
    closeBtn.addEventListener('mouseleave', () => { closeBtn.style.color = '#CCCCCC'; });
    this.panel.appendChild(closeBtn);

    // Title
    const title = document.createElement('div');
    title.textContent = 'Soporte de Pociones';
    title.style.cssText = `
      font-size: 15px; font-weight: bold; margin-bottom: 12px;
      color: #E8D8C0;
      text-shadow: 1px 1px 0 #000;
      text-align: center;
    `;
    this.panel.appendChild(title);

    // === Brewing area ===
    const brewArea = document.createElement('div');
    brewArea.style.cssText = `
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
      margin-bottom: 10px;
      padding: 10px 20px;
      min-width: ${COLS * (SLOT_SIZE + GAP)}px;
    `;

    // Left column: ingredient (top), bottle (middle), fuel (bottom)
    const leftCol = document.createElement('div');
    leftCol.style.cssText = `
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 8px;
    `;

    // Ingredient label + slot
    const ingLabel = document.createElement('div');
    ingLabel.textContent = 'Ingrediente';
    ingLabel.style.cssText = 'font-size:10px;color:#AAA;';
    leftCol.appendChild(ingLabel);

    this.ingredientSlotEl = this._createSlot('#5A5A5A');
    this.ingredientSlotEl.addEventListener('click', () => this._onBrewSlotClick('ingredient'));
    this.ingredientSlotEl.addEventListener('contextmenu', (e) => { e.preventDefault(); this._onBrewSlotRightClick('ingredient'); });
    leftCol.appendChild(this.ingredientSlotEl);

    // Bottle label + slot
    const bottleLabel = document.createElement('div');
    bottleLabel.textContent = 'Botella';
    bottleLabel.style.cssText = 'font-size:10px;color:#AAA;margin-top:4px;';
    leftCol.appendChild(bottleLabel);

    this.bottleSlotEl = this._createSlot('#5A5A5A');
    this.bottleSlotEl.addEventListener('click', () => this._onBrewSlotClick('bottle'));
    this.bottleSlotEl.addEventListener('contextmenu', (e) => { e.preventDefault(); this._onBrewSlotRightClick('bottle'); });
    leftCol.appendChild(this.bottleSlotEl);

    // Fuel label + slot
    const fuelLabel = document.createElement('div');
    fuelLabel.textContent = 'Combustible';
    fuelLabel.style.cssText = 'font-size:10px;color:#AAA;margin-top:4px;';
    leftCol.appendChild(fuelLabel);

    this.fuelSlotEl = this._createSlot('#4A3A2A');
    this.fuelSlotEl.addEventListener('click', () => this._onBrewSlotClick('fuel'));
    this.fuelSlotEl.addEventListener('contextmenu', (e) => { e.preventDefault(); this._onBrewSlotRightClick('fuel'); });
    leftCol.appendChild(this.fuelSlotEl);

    brewArea.appendChild(leftCol);

    // Center: progress arrow + bubbles
    const centerCol = document.createElement('div');
    centerCol.style.cssText = 'display:flex;flex-direction:column;align-items:center;gap:4px;';

    // Progress bar
    this.progressBar = document.createElement('div');
    this.progressBar.style.cssText = `
      width: 50px;
      height: 20px;
      background: #4A4A4A;
      position: relative;
      border: 2px solid #3A3A3A;
      overflow: hidden;
    `;

    this.progressFill = document.createElement('div');
    this.progressFill.style.cssText = `
      position: absolute;
      top: 0; left: 0;
      width: 0%;
      height: 100%;
      background: #7CAFC6;
      transition: width 0.1s linear;
    `;
    this.progressBar.appendChild(this.progressFill);

    const arrowSymbol = document.createElement('div');
    arrowSymbol.style.cssText = `
      position: absolute;
      top: 0; left: 0; width: 100%; height: 100%;
      display: flex; align-items: center; justify-content: center;
      font-size: 14px; color: #E0E0E0;
      pointer-events: none; z-index: 1;
    `;
    arrowSymbol.textContent = '\u27A1';
    this.progressBar.appendChild(arrowSymbol);

    centerCol.appendChild(this.progressBar);

    // Status text
    this.statusText = document.createElement('div');
    this.statusText.style.cssText = 'font-size:10px;color:#888;text-align:center;min-height:14px;';
    this.statusText.textContent = '';
    centerCol.appendChild(this.statusText);

    brewArea.appendChild(centerCol);

    // Right: output slot
    const rightCol = document.createElement('div');
    rightCol.style.cssText = 'display:flex;flex-direction:column;align-items:center;gap:8px;';

    const outLabel = document.createElement('div');
    outLabel.textContent = 'Resultado';
    outLabel.style.cssText = 'font-size:10px;color:#AAA;';
    rightCol.appendChild(outLabel);

    this.outputSlotEl = this._createSlot('#3A5A3A');
    this.outputSlotEl.style.width = (SLOT_SIZE + 8) + 'px';
    this.outputSlotEl.style.height = (SLOT_SIZE + 8) + 'px';
    this.outputSlotEl.addEventListener('click', () => this._onOutputClick());
    this.outputSlotEl.addEventListener('contextmenu', (e) => { e.preventDefault(); this._onOutputRightClick(); });
    rightCol.appendChild(this.outputSlotEl);

    brewArea.appendChild(rightCol);
    this.panel.appendChild(brewArea);

    // Brew button
    this.brewBtn = document.createElement('div');
    this.brewBtn.textContent = 'Preparar';
    this.brewBtn.style.cssText = `
      text-align: center;
      padding: 6px 16px;
      margin: 0 auto 10px auto;
      background: #4A6A4A;
      border: 2px solid #2A4A2A;
      border-top-color: #6A8A6A; border-left-color: #6A8A6A;
      color: #E0E0E0;
      font-size: 13px;
      font-family: 'Courier New', monospace;
      cursor: pointer;
      user-select: none;
      width: fit-content;
    `;
    this.brewBtn.addEventListener('click', () => this._startBrewing());
    this.brewBtn.addEventListener('mouseenter', () => { this.brewBtn.style.background = '#5A8A5A'; });
    this.brewBtn.addEventListener('mouseleave', () => { this.brewBtn.style.background = '#4A6A4A'; });
    this.panel.appendChild(this.brewBtn);

    // Separator
    const sep = document.createElement('div');
    sep.style.cssText = 'height: 2px; background: #5A5A5A; margin: 6px 0;';
    this.panel.appendChild(sep);

    // Player inventory label
    const invLabel = document.createElement('div');
    invLabel.textContent = 'Inventario';
    invLabel.style.cssText = 'font-size: 12px; margin-bottom: 4px; color: #AAAAAA;';
    this.panel.appendChild(invLabel);

    // Player storage (27 slots: unified indices 9-35)
    this.playerGrid = document.createElement('div');
    this.playerGrid.style.cssText = `
      display: grid;
      grid-template-columns: repeat(${COLS}, ${SLOT_SIZE}px);
      gap: ${GAP}px;
      margin-bottom: 6px;
    `;
    this.playerSlots = [];
    for (let i = 0; i < 27; i++) {
      const slot = this._createSlot('#5A5A5A');
      slot.addEventListener('click', () => this._onPlayerSlotClick(i + 9));
      slot.addEventListener('contextmenu', (e) => { e.preventDefault(); this._onPlayerSlotRightClick(i + 9); });
      this.playerGrid.appendChild(slot);
      this.playerSlots.push(slot);
    }
    this.panel.appendChild(this.playerGrid);

    // Player hotbar (9 slots: unified indices 0-8)
    const hotbarSep = document.createElement('div');
    hotbarSep.style.cssText = 'height: 2px; background: #5A5A5A; margin: 4px 0;';
    this.panel.appendChild(hotbarSep);

    this.hotbarGrid = document.createElement('div');
    this.hotbarGrid.style.cssText = `
      display: grid;
      grid-template-columns: repeat(${COLS}, ${SLOT_SIZE}px);
      gap: ${GAP}px;
    `;
    this.hotbarSlots = [];
    for (let i = 0; i < 9; i++) {
      const slot = this._createSlot('#5A5A5A');
      slot.addEventListener('click', () => this._onPlayerSlotClick(i));
      slot.addEventListener('contextmenu', (e) => { e.preventDefault(); this._onPlayerSlotRightClick(i); });
      this.hotbarGrid.appendChild(slot);
      this.hotbarSlots.push(slot);
    }
    this.panel.appendChild(this.hotbarGrid);

    // Held item cursor
    this.cursorEl = document.createElement('div');
    this.cursorEl.style.cssText = `
      position: fixed; pointer-events: none; z-index: 600;
      width: ${SLOT_SIZE}px; height: ${SLOT_SIZE}px;
      display: none;
    `;
    document.body.appendChild(this.cursorEl);

    this.overlay.appendChild(this.panel);
    document.body.appendChild(this.overlay);

    // Track mouse for held item
    this._onMouseMove = (e) => {
      if (!this.isOpen) return;
      this.cursorEl.style.left = (e.clientX - SLOT_SIZE / 2) + 'px';
      this.cursorEl.style.top = (e.clientY - SLOT_SIZE / 2) + 'px';
    };
    // mousemove and keydown listeners added in open(), removed in close()

    // ESC key handler
    this._escHandler = (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        e.preventDefault();
        e.stopPropagation();
        this.close();
      }
    };
  }

  // ─── Slot factory ──────────────────────────────────────────
  _createSlot(bgColor) {
    const slot = document.createElement('div');
    slot.style.cssText = `
      width: ${SLOT_SIZE}px; height: ${SLOT_SIZE}px;
      background: ${bgColor || '#5A5A5A'};
      border: 2px solid #373737;
      border-top-color: #8A8A8A; border-left-color: #8A8A8A;
      position: relative; cursor: pointer;
      display: flex; align-items: center; justify-content: center;
    `;
    return slot;
  }

  // ─── Helpers ───────────────────────────────────────────────
  _itemsMatch(a, b) {
    if (!a || !b) return false;
    if (a.blockId !== undefined && b.blockId !== undefined) return a.blockId === b.blockId;
    if (a.itemId !== undefined && b.itemId !== undefined) return a.itemId === b.itemId;
    return false;
  }

  _isEmpty(item) {
    return !item || (!item.count) || (item.blockId === 0 && item.itemId === undefined);
  }

  _getBrewSlot(name) {
    switch (name) {
      case 'ingredient': return this.ingredientSlot;
      case 'bottle': return this.bottleSlot;
      case 'fuel': return this.fuelSlot;
      default: return null;
    }
  }

  _setBrewSlot(name, item) {
    switch (name) {
      case 'ingredient': this.ingredientSlot = item; break;
      case 'bottle': this.bottleSlot = item; break;
      case 'fuel': this.fuelSlot = item; break;
    }
  }

  // ─── Brew slot click/right-click ──────────────────────────
  _onBrewSlotClick(name) {
    if (this._brewing) return; // Don't allow changes while brewing
    const current = this._getBrewSlot(name);
    if (this.heldItem) {
      if (this._isEmpty(current)) {
        this._setBrewSlot(name, { ...this.heldItem });
        this.heldItem = null;
      } else if (this._itemsMatch(current, this.heldItem)) {
        const space = MAX_STACK_SIZE - current.count;
        const transfer = Math.min(space, this.heldItem.count);
        current.count += transfer;
        this.heldItem.count -= transfer;
        if (this.heldItem.count <= 0) this.heldItem = null;
      } else {
        const tmp = current;
        this._setBrewSlot(name, { ...this.heldItem });
        this.heldItem = tmp;
      }
    } else if (!this._isEmpty(current)) {
      this.heldItem = { ...current };
      this._setBrewSlot(name, null);
    }
    this._render();
  }

  _onBrewSlotRightClick(name) {
    if (this._brewing) return;
    const current = this._getBrewSlot(name);
    if (this.heldItem) {
      if (this._isEmpty(current)) {
        this._setBrewSlot(name, { ...this.heldItem, count: 1 });
        this.heldItem.count--;
        if (this.heldItem.count <= 0) this.heldItem = null;
      } else if (this._itemsMatch(current, this.heldItem) && current.count < MAX_STACK_SIZE) {
        current.count++;
        this.heldItem.count--;
        if (this.heldItem.count <= 0) this.heldItem = null;
      }
    } else if (!this._isEmpty(current)) {
      const half = Math.ceil(current.count / 2);
      this.heldItem = { ...current, count: half };
      current.count -= half;
      if (current.count <= 0) this._setBrewSlot(name, null);
    }
    this._render();
  }

  // ─── Output slot: only pick up ────────────────────────────
  _onOutputClick() {
    if (this.heldItem) {
      if (this._itemsMatch(this.heldItem, this.outputSlot) && !this._isEmpty(this.outputSlot)) {
        const space = MAX_STACK_SIZE - this.heldItem.count;
        const transfer = Math.min(space, this.outputSlot.count);
        this.heldItem.count += transfer;
        this.outputSlot.count -= transfer;
        if (this.outputSlot.count <= 0) this.outputSlot = null;
      }
    } else if (!this._isEmpty(this.outputSlot)) {
      this.heldItem = { ...this.outputSlot };
      this.outputSlot = null;
    }
    this._render();
  }

  _onOutputRightClick() {
    if (!this.heldItem && !this._isEmpty(this.outputSlot)) {
      const half = Math.ceil(this.outputSlot.count / 2);
      this.heldItem = { ...this.outputSlot, count: half };
      this.outputSlot.count -= half;
      if (this.outputSlot.count <= 0) this.outputSlot = null;
    } else if (this.heldItem && this._itemsMatch(this.heldItem, this.outputSlot) && !this._isEmpty(this.outputSlot)) {
      if (this.heldItem.count < MAX_STACK_SIZE) {
        this.heldItem.count++;
        this.outputSlot.count--;
        if (this.outputSlot.count <= 0) this.outputSlot = null;
      }
    }
    this._render();
  }

  // ─── Player slot click/right-click (same as FurnaceScreen) ─
  _onPlayerSlotClick(idx) {
    const current = this.inventory.getUnifiedSlot(idx);
    if (this.heldItem) {
      if (this._isEmpty(current)) {
        this.inventory.setUnifiedSlot(idx, { ...this.heldItem });
        this.heldItem = null;
      } else if (this._itemsMatch(current, this.heldItem)) {
        const space = MAX_STACK_SIZE - current.count;
        const transfer = Math.min(space, this.heldItem.count);
        current.count += transfer;
        this.heldItem.count -= transfer;
        if (this.heldItem.count <= 0) this.heldItem = null;
      } else {
        const tmp = { ...current };
        this.inventory.setUnifiedSlot(idx, { ...this.heldItem });
        this.heldItem = tmp;
      }
    } else if (!this._isEmpty(current)) {
      this.heldItem = { ...current };
      this.inventory.setUnifiedSlot(idx, null);
    }
    this._render();
  }

  _onPlayerSlotRightClick(idx) {
    const current = this.inventory.getUnifiedSlot(idx);
    if (this.heldItem) {
      if (this._isEmpty(current)) {
        this.inventory.setUnifiedSlot(idx, { ...this.heldItem, count: 1 });
        this.heldItem.count--;
        if (this.heldItem.count <= 0) this.heldItem = null;
      } else if (this._itemsMatch(current, this.heldItem) && current.count < MAX_STACK_SIZE) {
        current.count++;
        this.heldItem.count--;
        if (this.heldItem.count <= 0) this.heldItem = null;
      }
    } else if (!this._isEmpty(current)) {
      const half = Math.ceil(current.count / 2);
      this.heldItem = { ...current, count: half };
      current.count -= half;
      if (current.count <= 0) this.inventory.setUnifiedSlot(idx, null);
    }
    this._render();
  }

  // ─── Brewing logic ────────────────────────────────────────
  _startBrewing() {
    if (this._brewing) return;

    // Check we have a valid recipe
    const bottleId = this.bottleSlot ? this.bottleSlot.itemId : null;
    const ingredientId = this.ingredientSlot ? this.ingredientSlot.itemId : null;
    if (bottleId === null || ingredientId === null) {
      this.statusText.textContent = 'Faltan ingredientes';
      return;
    }

    const recipe = findRecipe(bottleId, ingredientId);
    if (!recipe) {
      this.statusText.textContent = 'Receta no válida';
      return;
    }

    // Check fuel
    if (!this.fuelSlot || this.fuelSlot.itemId !== ItemId.BLAZE_POWDER || this.fuelSlot.count <= 0) {
      this.statusText.textContent = 'Necesitas Polvo de Blaze';
      return;
    }

    // Check output slot is empty or can stack
    if (this.outputSlot) {
      if (this.outputSlot.itemId !== recipe.result || this.outputSlot.count >= MAX_STACK_SIZE) {
        this.statusText.textContent = 'El resultado no cabe';
        return;
      }
    }

    this._brewing = true;
    this._brewTimer = 0;
    this._brewProgress = 0;
    this._currentRecipe = recipe;
    this.statusText.textContent = 'Preparando...';
    this._render();
  }

  /**
   * Call from game loop to tick the brewing timer.
   * @param {number} dt - Delta time in seconds
   */
  updateBrewing(dt) {
    if (!this._brewing || !this.isOpen) return;

    this._brewTimer += dt;
    this._brewProgress = Math.min(1, this._brewTimer / BREW_TIME);
    this.progressFill.style.width = (this._brewProgress * 100) + '%';

    if (this._brewTimer >= BREW_TIME) {
      this._completeBrewing();
    }
  }

  _completeBrewing() {
    const recipe = this._currentRecipe;
    if (!recipe) { this._brewing = false; return; }

    // Consume one bottle
    this.bottleSlot.count--;
    if (this.bottleSlot.count <= 0) this.bottleSlot = null;

    // Consume one ingredient
    this.ingredientSlot.count--;
    if (this.ingredientSlot.count <= 0) this.ingredientSlot = null;

    // Consume one fuel
    this.fuelSlot.count--;
    if (this.fuelSlot.count <= 0) this.fuelSlot = null;

    // Produce output
    if (this.outputSlot && this.outputSlot.itemId === recipe.result) {
      this.outputSlot.count++;
    } else {
      this.outputSlot = { itemId: recipe.result, count: 1 };
    }

    this._brewing = false;
    this._brewTimer = 0;
    this._brewProgress = 0;
    this._currentRecipe = null;
    this.progressFill.style.width = '0%';
    this.statusText.textContent = 'Listo!';
    this._render();
  }

  // ─── Open / Close / Toggle ────────────────────────────────
  open() {
    this.isOpen = true;
    this.overlay.style.display = 'flex';
    this.heldItem = null;
    this.cursorEl.style.display = 'none';
    this._brewing = false;
    this._brewTimer = 0;
    this._brewProgress = 0;
    this.progressFill.style.width = '0%';
    this.statusText.textContent = '';
    document.addEventListener('mousemove', this._onMouseMove);
    document.addEventListener('keydown', this._escHandler);
    this._render();
  }

  close() {
    if (!this.isOpen) return;

    // Stop brewing
    this._brewing = false;

    // Return held item to player inventory
    if (this.heldItem) {
      if (this.heldItem.blockId !== undefined) {
        this.inventory.addBlock(this.heldItem.blockId);
      } else if (this.heldItem.itemId !== undefined) {
        this.inventory.addItem(this.heldItem.itemId, this.heldItem.count);
      }
      this.heldItem = null;
    }

    // Return brewing slots to player inventory
    this._returnSlotToInventory(this.ingredientSlot);
    this._returnSlotToInventory(this.bottleSlot);
    this._returnSlotToInventory(this.fuelSlot);
    this._returnSlotToInventory(this.outputSlot);

    this.ingredientSlot = null;
    this.bottleSlot = null;
    this.fuelSlot = null;
    this.outputSlot = null;

    this.isOpen = false;
    this.overlay.style.display = 'none';
    this.cursorEl.style.display = 'none';
    document.removeEventListener('mousemove', this._onMouseMove);
    document.removeEventListener('keydown', this._escHandler);
  }

  toggle() {
    if (this.isOpen) this.close();
    else this.open();
  }

  _returnSlotToInventory(item) {
    if (!item || !item.count) return;
    if (item.itemId !== undefined) {
      this.inventory.addItem(item.itemId, item.count);
    } else if (item.blockId !== undefined) {
      for (let c = 0; c < item.count; c++) {
        this.inventory.addBlock(item.blockId);
      }
    }
  }

  // ─── Rendering ────────────────────────────────────────────
  _render() {
    // Brew slots
    this._renderSlot(this.ingredientSlotEl, this.ingredientSlot);
    this._renderSlot(this.bottleSlotEl, this.bottleSlot);
    this._renderSlot(this.fuelSlotEl, this.fuelSlot);
    this._renderSlot(this.outputSlotEl, this.outputSlot);

    // Progress
    this.progressFill.style.width = (this._brewProgress * 100) + '%';

    // Brew button state
    if (this._brewing) {
      this.brewBtn.style.opacity = '0.5';
      this.brewBtn.style.cursor = 'default';
    } else {
      this.brewBtn.style.opacity = '1';
      this.brewBtn.style.cursor = 'pointer';
    }

    // Player storage (unified indices 9-35)
    for (let i = 0; i < 27; i++) {
      this._renderSlot(this.playerSlots[i], this.inventory.getUnifiedSlot(i + 9));
    }
    // Hotbar (unified indices 0-8)
    for (let i = 0; i < 9; i++) {
      this._renderSlot(this.hotbarSlots[i], this.inventory.getUnifiedSlot(i));
    }

    // Held item on cursor
    if (this.heldItem) {
      this.cursorEl.style.display = 'block';
      this.cursorEl.innerHTML = '';
      this._renderSlot(this.cursorEl, this.heldItem);
      this.cursorEl.style.background = 'transparent';
      this.cursorEl.style.border = 'none';
    } else {
      this.cursorEl.style.display = 'none';
    }
  }

  _renderSlot(slotEl, item) {
    slotEl.innerHTML = '';
    if (this._isEmpty(item)) return;

    if (item.itemId !== undefined) {
      slotEl.innerHTML = renderItemIcon(item.itemId, 32);
    } else {
      slotEl.innerHTML = renderBlockIcon(item.blockId, 32);
    }

    if (item.count > 1) {
      const countEl = document.createElement('div');
      countEl.textContent = item.count;
      countEl.style.cssText = `
        position: absolute; bottom: 1px; right: 3px;
        font-size: 11px; font-weight: bold; color: white;
        text-shadow: 1px 1px 0 #000;
        pointer-events: none;
      `;
      slotEl.appendChild(countEl);
    }
  }
}
